import classNames from 'classnames'

interface CarouselDotsProps {
  count: number
  currentIndex: number
  onDotClick: (index: number) => void
  /** Duration in ms for the fill animation. Set to 0 to disable. @default 6500 */
  autoAdvanceDuration?: number
  /** @default 'dark' — white active / gray-500 inactive (for dark backgrounds) */
  theme?: 'dark' | 'light'
  className?: string
}

const themeStyles = {
  dark: { active: 'bg-white', inactive: 'bg-core-gray-500', fill: 'bg-white' },
  light: { active: 'bg-core-gray-800', inactive: 'bg-core-gray-300', fill: 'bg-core-gray-800' },
}

export function CarouselDots({
  count,
  currentIndex,
  onDotClick,
  autoAdvanceDuration = 6500,
  theme = 'dark',
  className,
}: CarouselDotsProps) {
  const colors = themeStyles[theme]

  return (
    <ul className={classNames('flex flex-row gap-1', className)} role="tablist">
      {Array.from({ length: count }, (_, index) => {
        const isActive = index === currentIndex
        return (
          <li
            key={index}
            role="tab"
            aria-selected={isActive}
            aria-label={`Slide ${index + 1}`}
            onClick={() => onDotClick(index)}
            className="flex cursor-pointer items-center justify-center p-1"
          >
            <div
              className={classNames('relative h-2.5 overflow-hidden rounded-full transition-all duration-300', {
                'w-8': isActive,
                'w-2.5': !isActive,
                [colors.inactive]: !isActive,
              })}
            >
              {isActive && (
                <>
                  <div className={classNames('absolute inset-0', colors.inactive)} />
                  <div
                    key={currentIndex}
                    className={classNames('absolute inset-0 rounded-full', colors.fill, {
                      'animate-dot-fill': autoAdvanceDuration > 0,
                    })}
                    style={
                      autoAdvanceDuration > 0
                        ? ({ '--dot-fill-duration': `${autoAdvanceDuration}ms` } as React.CSSProperties)
                        : undefined
                    }
                  />
                </>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
